/*
 * The dense composition set — the primitives the job-module mockups are built out of.
 *
 * **This is a composition problem, not a palette one.** Reading the two reference mockups against
 * `tokens.css`, every colour, every type step and every semantic they use already exists here. What
 * did not exist is the *shell*: a container that can carry a header, an alert, a body and a footer
 * as one object with one outline and one set of hairlines between them. `Card` cannot, because
 * `Card` pads itself — so a card holding a full-bleed alert bar has to fight its own padding, and a
 * card inside a card doubles every border. `Panel` owns the outline and owns nothing else; each
 * `PanelBand` brings its own padding and its own bottom hairline. One panel, no nesting.
 *
 * **What is deliberately not here.** The mockups' `SeverityDot` and `DispositionStamp` are real
 * devices and they are not in this file, because nothing on the two screens this slice builds uses
 * them. `components.tsx` opens by saying the package is "small on purpose… nothing is here
 * speculatively", and a primitive with no caller is a primitive whose API was guessed rather than
 * found. They land with the screen that needs them.
 *
 * **And one divergence, stated rather than absorbed.** The mockups run 10.5–13px text. Design
 * language §1.2 sets 13px as the floor — "nothing renders smaller, including captions and chips" —
 * and that floor is an accessibility decision, not a stylistic one. So `Dense` buys its density from
 * *spacing* alone: the row padding tightens, the type does not. The result is slightly less dense
 * than the reference and legible to people the reference would lose.
 */

import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import type { StatusTone } from './components.js';

/*
 * Exported as a runtime list, not only as a type, because the failure mode is a slot that type-checks
 * and renders no padding. `panel.test.ts` walks this list against the stylesheet, so a sixth slot
 * added here without a rule is a red test rather than a band that silently sits flush against the
 * one above it.
 */
export const PANEL_BAND_SLOTS = ['head', 'toolbar', 'body', 'foot'] as const;
export type PanelBandSlot = (typeof PANEL_BAND_SLOTS)[number];

export interface PanelProps {
  /**
   * `advisory` draws the outline dashed.
   *
   * The mockups' one free semantic channel: **dashed is advisory, pending or inherited; solid is
   * committed.** It costs one convention and it says something no colour in the palette says, because
   * every tone here already means "how is this going" rather than "is this ours yet". A job showing
   * the plan it inherited is not in an attention state — it is in a *provisional* one, and the border
   * is what carries that.
   */
  tone?: 'default' | 'advisory';
  children: ReactNode;
}

/**
 * A bordered surface with no padding of its own.
 *
 * `overflow: hidden` plus a radius on the panel is what lets a band run full width to the panel's
 * edge and still be clipped to the corner — the alternative is every band repeating the corner radius
 * and getting one of them wrong.
 */
export function Panel({ tone = 'default', children }: PanelProps) {
  return (
    <section className={`tf-panel${tone === 'advisory' ? ' tf-panel--advisory' : ''}`}>
      {children}
    </section>
  );
}

export interface PanelBandProps {
  /**
   * Which band this is. Each has its own padding and its own ground; the last band in a panel drops
   * its hairline, which is a CSS rule rather than something callers have to remember.
   */
  slot?: PanelBandSlot;
  children: ReactNode;
}

export function PanelBand({ slot = 'body', children }: PanelBandProps) {
  return <div className={`tf-band tf-band--${slot}`}>{children}</div>;
}

export interface InlineAlertProps {
  tone?: StatusTone;
  /** Cause and effect, in one sentence. If it needs two, it is not an inline alert. */
  children: ReactNode;
  /** The remedy, inline — the whole point of the device is that the fix is in the bar. */
  action?: ReactNode;
}

/**
 * The full-bleed bar inside a panel: what changed, what it means, and what to do about it.
 *
 * Distinct from `Banner`, which floats in a page's flow and is about *something that just happened*.
 * This is about a state the panel is in and stays in until somebody acts — "this artifact is stale",
 * "this plan is inherited". `role="alert"` only on `critical`, following `Banner`'s reasoning: an
 * assertive live region for a standing condition interrupts a screen reader for something that was
 * already true before the page loaded.
 *
 * **It is a band, not something placed inside one**, which is why there is no `alert` slot: it brings
 * the same padding and the same hairline as its siblings and sits directly under the panel. Two ways
 * to render one bar is how a design system grows a wrapper nobody can choose between.
 */
export function InlineAlert({ tone = 'info', children, action }: InlineAlertProps) {
  return (
    <div
      className={`tf-inline-alert tf-inline-alert--${tone}`}
      role={tone === 'critical' ? 'alert' : undefined}
    >
      <span className="tf-inline-alert__body">{children}</span>
      {action ? <span className="tf-inline-alert__action">{action}</span> : null}
    </div>
  );
}

/*
 * ── Grounding ───────────────────────────────────────────────────────────────────────────────────
 *
 * The mockups mark facts with where they came from — JD, CLIENT, MODEL. Generalised here to the
 * question underneath it, *how do we know this*, because the same device answers it for an inherited
 * setting as for an extracted one, and shipping two chip families for one question is how a design
 * system grows a lookalike component nobody can choose between.
 */
export const GROUNDINGS = ['stated', 'inherited', 'default', 'derived', 'model'] as const;
export type Grounding = (typeof GROUNDINGS)[number];

export interface GroundingChipProps {
  origin: Grounding;
  /**
   * Required, and a string. Same rule as `StatusChip`: colour never carries the meaning alone, and a
   * chip that *could* be given only an icon eventually is.
   */
  label: string;
}

export function GroundingChip({ origin, label }: GroundingChipProps) {
  return <span className={`tf-grounding tf-grounding--${origin}`}>{label}</span>;
}

export interface MetaLineProps {
  /**
   * The second line of a two-line row, as separate items — the separator is drawn by CSS so a caller
   * cannot ship a row with a trailing middot, and so the parts stay addressable for a screen reader.
   *
   * Falsy entries are dropped, so a caller can write the list positionally without assembling it
   * conditionally first. Empty items would otherwise render as bare separators.
   */
  items: readonly ReactNode[];
}

/**
 * Quantified metadata under a row's title — "6 questions · with rubrics", "3 rounds · 135 minutes".
 *
 * The single highest-value device in either mockup: it is what turns a list into a status board,
 * because line two answers "do I need to open this" without opening it. The discipline is that every
 * item is a *quantity or a state*, never a restatement of the title.
 */
export function MetaLine({ items }: MetaLineProps) {
  const shown = items.filter(Boolean);
  return (
    <span className="tf-meta">
      {shown.map((item, index) => (
        <span key={index} className="tf-meta__item">
          {item}
        </span>
      ))}
    </span>
  );
}

/*
 * ── Severity and disposition ────────────────────────────────────────────────────────────────────
 *
 * Held back from the first pass of this file because nothing used them; they arrive with §4's intake
 * verdict, which is the screen the mockups drew them for.
 *
 * They are deliberately **not** one generic `Chip`, and the intake row is why: a flag carries how
 * much it matters, where it came from, and what somebody decided about it, all on one line. A single
 * badge component would render three different questions in one visual language and the row would
 * read as mush.
 */

export const SEVERITIES = ['low', 'medium', 'high'] as const;
export type Severity = (typeof SEVERITIES)[number];

export interface SeverityDotProps {
  level: Severity;
  /**
   * Required, and rendered. Colour never carries meaning alone — the same rule `StatusChip` and
   * `StatusDot` follow, and the reason a graded scale needs it most: three tints of one hue are the
   * hardest thing on a screen to tell apart without text.
   */
  label: string;
}

/**
 * How much a flag matters, as a graded mark.
 *
 * **Grade shows in the fill as well as the colour** — hollow, half, solid — so the ranking survives
 * a monochrome screen and the two most common colour deficiencies. A scale that existed only as
 * three shades would be the one piece of this design that a colour-blind reader could not use, and
 * severity is precisely what they most need to scan.
 */
export function SeverityDot({ level, label }: SeverityDotProps) {
  return (
    <span className={`tf-severity tf-severity--${level}`}>
      <span className="tf-severity__mark" aria-hidden="true" />
      {label}
    </span>
  );
}

export interface DispositionStampProps {
  /** The word for what was decided — "Accepted", "Dismissed", "Resolved". Never an icon alone. */
  label: string;
  tone?: StatusTone;
  /** Who decided. Omitted when nobody did — a state the product has, and should not dress up. */
  actor?: string;
  /** When, already formatted for the locale. This component does no date maths. */
  when?: string;
}

/**
 * What somebody decided, who, and when — the three parts of a disposition, on one line.
 *
 * The mockups' device, and its value is that it makes the *absence* of a decision visible: a row
 * with no stamp is a row nobody has answered, which is the whole of §5.2's gate rendered without a
 * single count. `actor` and `when` are optional because a machine-written disposition has neither,
 * and inventing "System" for it would claim an accountability nobody holds.
 */
export function DispositionStamp({ label, tone = 'neutral', actor, when }: DispositionStampProps) {
  return (
    <span className={`tf-stamp tf-stamp--${tone}`}>
      <span className="tf-stamp__label">{label}</span>
      {actor ? <span className="tf-stamp__actor">{actor}</span> : null}
      {when ? <span className="tf-stamp__when">{when}</span> : null}
    </span>
  );
}

/*
 * Who is holding a candidature up — CO9 §6, and the reason it is a component rather than a tone on
 * `StatusChip`.
 *
 * A blocker answers a different question from a status. A status says what a row *is*; a blocker
 * says what it is *waiting on*, and the useful half of that answer is **whose door we knock on** —
 * the client, the candidate, ourselves, or the requisition. So the owner is a required prop and the
 * palette is its own (`--blocked-*`), rather than a caller choosing a tone and hoping the board
 * agrees with itself about which red means "us".
 */
export const BLOCKED_BY = ['client', 'candidate', 'us', 'job'] as const;
export type BlockedBy = (typeof BLOCKED_BY)[number];

export interface BlockerChipProps {
  owner: BlockedBy;
  /**
   * The agency's own word for this blocker — "Awaiting Client Feedback", "We Haven't Chased".
   * Required and a string, for the reason every label atom in this file states: colour never
   * carries the meaning alone (§2.3). Tenant vocabulary, so it arrives translated by nobody.
   */
  label: string;
  /**
   * How long, already formatted — "4d", "11 days". This component does no date maths and holds no
   * copy; the caller's locale decides how a duration reads.
   */
  detail?: string;
  /**
   * Drawn dashed, and the caller says why in `detail`. Two things inherit onto a card and neither
   * is the card's own: a frozen requisition, which is one fact about the job shown on every card in
   * it, and a person who is not answering her phone, which is true of her on every board at once.
   * Dashed is this package's one free semantic channel and already means *inherited or advisory*
   * on `Panel` and `GroundingChip`, so it costs nothing to learn twice.
   */
  inherited?: boolean;
}

/**
 * Why a candidature is not moving, and who is holding it up.
 *
 * **The owner shows in the mark's silhouette as well as its colour** — ring, dotted ring, square,
 * bar — for `SeverityDot`'s reason one step harder: four owners in a deliberately muted palette
 * cannot all be far apart in hue, and two of them are adjacent warm tones. The silhouette is what
 * survives a monochrome screenshot and the common colour deficiencies; the label is what survives
 * everything.
 *
 * **An absent chip is the meaningful state.** A card with no chip is moving normally, which is why
 * this renders nothing rather than a "not blocked" variant — a board where every card carries a
 * chip is a board where the chips have stopped meaning anything.
 */
export function BlockerChip({ owner, label, detail, inherited = false }: BlockerChipProps) {
  return (
    <span className={`tf-blocker tf-blocker--${owner}${inherited ? ' tf-blocker--inherited' : ''}`}>
      <span className="tf-blocker__mark" aria-hidden="true" />
      <span className="tf-blocker__label">{label}</span>
      {detail ? <span className="tf-blocker__detail">{detail}</span> : null}
    </span>
  );
}

export interface MatchScoreProps {
  /**
   * 0–100, or **null for not assessed** — and the null is the reason this is a component.
   *
   * CO-12 draws it and says why in one line: *"no score is a state, never a zero"*. A candidate
   * with no parsed CV has no number, and an empty cell reads as a zero to everybody who sees it —
   * which would sort a person we know nothing about to the bottom of every ranked list for a reason
   * about our data rather than about them. Every caller would have to remember that; this one
   * remembers it for them.
   */
  score: number | null;
  /** What to say when there is no score. The caller's locale owns the words. */
  notAssessedLabel: string;
  /** An accessible name, already composed — "Fitment 91 of 100, strong". */
  title?: string;
}

/**
 * How well somebody fits one requisition — M-48, CO-12 §1.
 *
 * **Opacity tracks the number, and the number is always written.** The mockup's legend says
 * *"opacity tracks the score"*, and that is a second channel rather than the channel: this package
 * does not let colour or weight carry meaning alone (§2.3), and a dot at 40% against one at 70% is
 * not a distinction anybody can read off a board. The digits are the fact; the dot is what makes a
 * column of them scannable.
 *
 * **Tabular numerals**, because the whole point of a score on a card is comparing it down a column,
 * and proportional digits make 91 and 72 different widths.
 *
 * The band is deliberately not drawn here. Four bands in colour on a board already carrying blocker
 * chips in four owners would be two palettes competing to mean something, and the board's colour
 * belongs to what is stuck rather than to what fits.
 */
export function MatchScore({ score, notAssessedLabel, title }: MatchScoreProps) {
  if (score === null) {
    return (
      <span className="tf-score tf-score--none" title={title}>
        <i className="tf-score__dot" aria-hidden="true" />
        <span className="tf-score__value">{notAssessedLabel}</span>
      </span>
    );
  }
  return (
    <span className="tf-score" title={title}>
      {/*
        Floored at a fifth so a weak score is still visibly *a* score rather than an absent one —
        the one thing the dot must never be confused with is the null state above it.
      */}
      <i
        className="tf-score__dot"
        aria-hidden="true"
        style={{ opacity: Math.max(0.2, score / 100) }}
      />
      <span className="tf-score__value">{Math.round(score)}</span>
    </span>
  );
}

/**
 * A density scope, applied to a region rather than passed to every component in it.
 *
 * A `density` prop on each primitive is the same decision taken twenty times, and it goes wrong the
 * first time somebody sets it on nine of ten rows. This re-declares the band spacing tokens for
 * everything inside it, so a panel becomes dense by being *placed* in one.
 */
export function Dense({ children }: { children: ReactNode }) {
  return <div className="tf-dense">{children}</div>;
}

/*
 * ── The reader's density ────────────────────────────────────────────────────────────────────────
 *
 * `Dense` above is a layout decision a screen makes about one region. This is the other axis, and it
 * is a *preference*: how much air somebody wants between the rows they read all day. The two compose
 * — see the note in panel.css — and neither replaces the other.
 */
export const DENSITIES = ['comfortable', 'compact'] as const;
export type DensityMode = (typeof DENSITIES)[number];

/*
 * The default must match the CSS, and it does: `--cell-block` and friends are declared at
 * `comfortable`'s values on `:root`, so a screen with no `Density` around it reads the same either
 * way. A mismatch here would show up as a KPI band on a screen that is drawn comfortable.
 */
const DensityContext = createContext<DensityMode>('comfortable');

/**
 * What density the surrounding scope is set to.
 *
 * Exists because two of the choices density governs cannot be made in CSS. The KPI strip and the KPI
 * band are different *elements* — four cards against four cells sharing one panel — not one element
 * at two paddings, so the call site has to branch, and it should branch on the same remembered
 * setting the spacing does rather than on a second switch nobody would keep in sync.
 */
export function useDensity(): DensityMode {
  return useContext(DensityContext);
}

/**
 * A density scope. Wrap a screen, not a component.
 *
 * Renders no box at all (`display: contents`) so it can go anywhere — including around a grid child,
 * which is where a wrapper div normally breaks the layout it was added to.
 */
export function Density({ mode, children }: { mode: DensityMode; children: ReactNode }) {
  return (
    <DensityContext.Provider value={mode}>
      <div className={`tf-density tf-density--${mode}`}>{children}</div>
    </DensityContext.Provider>
  );
}

/*
 * ── The divided KPI band ────────────────────────────────────────────────────────────────────────
 *
 * `KpiStrip` is four separate cards at roughly 120px tall. This is four cells sharing one panel with
 * hairlines between them, at roughly 52px. Both are correct: the strip leads a landing page, where
 * the numbers *are* the content; the band sits above a table it filters, where the numbers are the
 * table's index and taking a fifth of the viewport for them is what pushed the rows off the screen.
 */
export interface KpiCellProps {
  label: ReactNode;
  value: ReactNode;
  /** A qualifier beside the number — "of 24", "+3 this week". Never its own row. */
  detail?: ReactNode;
  tone?: 'default' | 'positive' | 'attention' | 'critical';
  /** Marks the cell as the current selection when the band is acting as a filter. */
  active?: boolean;
  onClick?: () => void;
}

export function KpiCell({ label, value, detail, tone = 'default', active, onClick }: KpiCellProps) {
  const content = (
    <>
      <span className="tf-kpi-cell__label">{label}</span>
      <span className="tf-kpi-cell__row">
        <span className="tf-kpi-cell__value">{value}</span>
        {detail ? <span className="tf-kpi-cell__detail">{detail}</span> : null}
      </span>
    </>
  );

  const className = [
    'tf-kpi-cell',
    tone === 'default' ? '' : `tf-kpi-cell--${tone}`,
    onClick ? 'tf-kpi-cell--interactive' : '',
    active ? 'tf-kpi-cell--active' : '',
  ]
    .filter(Boolean)
    .join(' ');

  // A real button when it acts like one, and `aria-pressed` rather than a class alone: a filter that
  // is on has to announce that it is on, or a screen reader user cannot tell why the table is short.
  return onClick ? (
    <button type="button" className={className} onClick={onClick} aria-pressed={active ?? false}>
      {content}
    </button>
  ) : (
    <div className={className}>{content}</div>
  );
}

export function KpiBand({ children }: { children: ReactNode }) {
  return <div className="tf-kpi-band">{children}</div>;
}

export interface ProvenanceMarkProps {
  /** True when the fixture answered. The caller derives it; this package knows no adapter names. */
  sample: boolean;
  /** Localised by the caller — this package holds no catalogue. */
  labels: {
    /** Shown when `sample`. Something like "Sample answer". */
    sample: string;
    /** Shown otherwise — "Answered by TalentAI Assist", already composed with the assistant's name. */
    answeredBy: string;
  };
}

/**
 * Whether a person is reading a real answer or a placeholder — AI requirements §1's *"sample mode is
 * labelled on screen"*.
 *
 * **The most under-rated element in this package, and it earns that by being unpleasant.** The
 * fixture composes plausible prose out of the record it was given; that is what makes it useful for
 * building screens, and it is exactly what makes it dangerous unlabelled. A recruiter reading an
 * intake verdict has no way to tell a judgement from a rehearsal, and neither has anybody trying to
 * verify that a newly configured provider is actually being reached.
 *
 * So the sample state is deliberately the louder of the two. It is a warning tone, it says the word
 * *sample*, and it does not shrink politely into the corner — a marker somebody can miss is a marker
 * that has not been drawn. The live state is quiet by comparison, because a working system should
 * not congratulate itself on every paragraph.
 *
 * ── It named the provider until 31 Aug 2026 ────────────────────────────────────────────────────
 *
 * It took `adapter` and `model` and rendered *"Answered by mistral · mistral-small-latest"*. Removed
 * on the product owner's instruction: **an agency is never shown which model is being used.** The
 * assistant has one name, which the caller supplies already composed, and which lives in one place
 * in the catalogue so that settling the platform's name is one string.
 *
 * The props are a boolean rather than a narrowed adapter string on purpose. A component that still
 * took a name would be a component somebody widens back the first time a name looks useful.
 */
export function ProvenanceMark({ sample, labels }: ProvenanceMarkProps) {
  if (sample) {
    return <span className="tf-provenance tf-provenance--sample">{labels.sample}</span>;
  }

  return <span className="tf-provenance tf-provenance--live">{labels.answeredBy}</span>;
}

export interface StepTrackerStep {
  readonly id: string;
  /** The agency's own word for this position. Tenant vocabulary, translated by nobody. */
  readonly label: string;
  /** When it reached here, already formatted. This component does no date maths. */
  readonly reached?: string;
  readonly state: 'done' | 'here' | 'ahead';
}

export interface StepTrackerProps {
  readonly steps: readonly StepTrackerStep[];
  /** Announced on the current step, e.g. "here". Copy is the caller's. */
  readonly hereLabel: string;
}

/**
 * How far a candidature got, as a line rather than a list.
 *
 * ── Why a tracker and not the history it sits above ─────────────────────────────────────────────
 *
 * The history answers *what happened, in order*. This answers a different question — **how far**,
 * and how much is left — and it answers it at a glance, which a reverse-chronological list of
 * moves cannot. Both are on the drawer because a recruiter asks both, at different moments: the
 * tracker while deciding whether this is worth chasing, the history while working out why it is
 * where it is.
 *
 * ── The state that matters is `ahead` ───────────────────────────────────────────────────────────
 *
 * Drawing the stages *not yet reached* is what makes this a measure rather than a decoration. A
 * tracker showing only where somebody has been is a history with worse typography; showing the
 * remaining steps is what turns "offered" into "offered, two to go".
 *
 * **And it never re-orders itself for an ended candidature.** One that died at shortlist shows
 * shortlist as `here`, with everything after it `ahead` and unreached — because that is true, and
 * it is the whole of change order 09 §2(a) in one line of markup. A tracker that collapsed to the
 * ending would destroy on screen exactly what the schema was changed to preserve.
 *
 * Colour is not the channel: `done` is filled, `here` is ringed and carries a word, `ahead` is
 * hollow. §2.3 — the state is legible in monochrome and to a screen reader, which reads the list
 * in order and hears the current one named.
 */
export function StepTracker({ steps, hereLabel }: StepTrackerProps) {
  return (
    <ol className="tf-tracker">
      {steps.map((step) => (
        <li key={step.id} className={`tf-tracker__step tf-tracker__step--${step.state}`}>
          <span className="tf-tracker__dot" aria-hidden="true" />
          <span className="tf-tracker__label">{step.label}</span>
          {step.state === 'here' ? (
            <span className="tf-tracker__here">{hereLabel}</span>
          ) : step.reached ? (
            <span className="tf-tracker__when">{step.reached}</span>
          ) : null}
        </li>
      ))}
    </ol>
  );
}

export interface StatePillProps {
  /** The question this pill answers, in small caps above the value. */
  readonly eyebrow: string;
  readonly value: ReactNode;
  /** Draws the pill in a blocker owner's colour. Omit for the neutral three. */
  readonly owner?: BlockedBy;
  readonly tone?: 'neutral' | 'ended';
}

/**
 * One of the four answers a candidature has, with the question written above it.
 *
 * ── Why the eyebrow is not optional ─────────────────────────────────────────────────────────────
 *
 * Change order 09's opening argument is that **one dropdown was answering four different
 * questions** — where it is, whether it is live, why it is not moving, and who is holding it up —
 * and that a reader could not tell which answer they were looking at. Four values in a row
 * reproduces exactly that problem in a nicer font. The question is what makes each one readable,
 * so it is a required prop rather than a nicety, and a pill cannot be constructed without one.
 */
export function StatePill({ eyebrow, value, owner, tone = 'neutral' }: StatePillProps) {
  const modifier = owner ? `tf-state-pill--${owner}` : `tf-state-pill--${tone}`;
  return (
    <span className={`tf-state-pill ${modifier}`}>
      <span className="tf-state-pill__eyebrow">{eyebrow}</span>
      <span className="tf-state-pill__value">{value}</span>
    </span>
  );
}
